/**
 * GardenStudio — Phase 5 View 2.
 *
 * Top: derived health summary (last run per type, schedules, pending creatives).
 * Then: pending review queue (only when there are any).
 * Then: run timeline with auto-refresh while any row is `running`.
 * Sidebar: Trigger Run actions per run type.
 *
 * The Phase 4 garden API returns full GardenRun records on list and uses
 * limit-based pagination (no `total`). We keep a "Load more" affordance
 * by stepping the limit if the user scrolls.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sprout,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Compass as CompassIcon,
  Workflow,
} from 'lucide-react';
import { clsx } from 'clsx';
import {
  gardenApi,
  type GardenRun,
  type GardenRunType,
  type ExtractionStatusResponse,
} from '@/services/api';
import {
  RunStatusBadge,
  RunTypeBadge,
} from '@/components/pdo/StatusBadges';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import {
  useResolvedClientUid,
  useFetch,
  formatDuration,
  formatDelta,
  formatNumber,
  timeAgo,
} from '@/components/pdo/utils';
import { RunDetailPanel } from './RunDetail';
import { TriggerRunDrawer } from './TriggerRunDrawer';
import { ReviewQueue } from './ReviewQueue';

const PAGE_SIZE = 50;

const RUN_TYPE_STAGES: Record<GardenRunType, number> = {
  creative_batch: 8,
  performance_refresh: 5,
  compass_directed: 9,
  scheduled_rebuild: 7,
  creative_memory_cycle: 3,
  strategic_rebuild: 6,
  creative_review_backfill: 4,
  creative_review_refresh: 4,
  creative_review_seed: 2,
  strategy_docket_refresh: 6,
};

function durationFromTimestamps(
  startedAt: string | null,
  finishedAt: string | null,
): number | null {
  if (!startedAt || !finishedAt) return null;
  const s = new Date(startedAt).getTime();
  const f = new Date(finishedAt).getTime();
  if (Number.isNaN(s) || Number.isNaN(f)) return null;
  return f - s;
}

export function GardenStudio() {
  const clientUid = useResolvedClientUid('/admin/garden');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trigger, setTrigger] = useState<GardenRunType | null>(null);
  const [limit, setLimit] = useState(PAGE_SIZE);

  const detailRunId = searchParams.get('run');

  const runsQuery = useFetch(
    () =>
      clientUid
        ? gardenApi.listRuns(clientUid, { limit })
        : Promise.resolve({ client_uid: '', count: 0, runs: [] }),
    [clientUid, limit],
  );

  const reviewQuery = useFetch(
    () =>
      clientUid
        ? gardenApi.listRuns(clientUid, {
            status: 'pending_review',
            limit: 100,
          })
        : Promise.resolve({ client_uid: '', count: 0, runs: [] }),
    [clientUid],
  );

  const schedulesQuery = useFetch(
    () =>
      clientUid
        ? gardenApi.schedules(clientUid)
        : Promise.resolve(null as never),
    [clientUid],
  );

  const extractionQuery = useFetch(
    () =>
      clientUid
        ? gardenApi.extractionStatus(clientUid)
        : Promise.resolve(null as unknown as ExtractionStatusResponse),
    [clientUid],
  );

  // Auto-refresh while any run is `running` or `pending`.
  useEffect(() => {
    if (!clientUid || !runsQuery.data) return;
    const inflight = runsQuery.data.runs.some(
      (r) => r.status === 'running' || r.status === 'pending',
    );
    if (!inflight) return;
    const id = setInterval(() => runsQuery.refetch(), 5_000);
    return () => clearInterval(id);
  }, [clientUid, runsQuery]);

  if (!clientUid) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Resolving client…
      </div>
    );
  }

  const openDetail = (runId: string) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('run', runId);
        return next;
      },
      { replace: false },
    );

  const closeDetail = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('run');
        return next;
      },
      { replace: true },
    );

  const refetchAll = () => {
    runsQuery.refetch();
    reviewQuery.refetch();
    schedulesQuery.refetch();
    extractionQuery.refetch();
  };

  const lastRunPerType = useMemo(() => {
    const map = new Map<GardenRunType, GardenRun>();
    for (const r of runsQuery.data?.runs ?? []) {
      const existing = map.get(r.run_type);
      const ts = r.started_at ? new Date(r.started_at).getTime() : 0;
      const ets = existing?.started_at
        ? new Date(existing.started_at).getTime()
        : 0;
      if (!existing || ts > ets) map.set(r.run_type, r);
    }
    return map;
  }, [runsQuery.data]);

  const pendingCreatives = extractionQuery.data?.counts.pending ?? 0;

  return (
    <div className="flex flex-col h-full">
      <header className="flex items-start justify-between gap-4 px-1 pb-4 border-b border-slate-200 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-brand-teal to-brand-green flex items-center justify-center text-navy-900 shadow-glow">
            <Sprout size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-navy-900">Knowledge Garden</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Live runs, review queue, and Compass evolution for{' '}
              <span className="font-mono text-navy-900">{clientUid}</span>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/admin/garden/${clientUid}/compass`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-purple-300 hover:text-purple-700"
          >
            <CompassIcon size={14} />
            Compass history
          </Link>
          <button
            type="button"
            onClick={refetchAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </header>

      <HealthBar
        lastRunPerType={lastRunPerType}
        pendingCreatives={pendingCreatives}
        schedule={schedulesQuery.data?.schedule}
      />

      {(reviewQuery.data?.runs.length ?? 0) > 0 && (
        <ReviewQueue
          clientUid={clientUid}
          runs={reviewQuery.data?.runs ?? []}
          onAction={refetchAll}
        />
      )}

      <div className="grid grid-cols-12 gap-4 mt-4">
        <div className="col-span-9">
          {runsQuery.error && (
            <ErrorBanner error={runsQuery.error} onRetry={runsQuery.refetch} />
          )}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Run</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Duration</th>
                  <th className="px-3 py-2 text-right">Δ Concepts</th>
                  <th className="px-3 py-2 text-right">Δ AUC</th>
                  <th className="px-3 py-2 text-left">Trigger</th>
                  <th className="px-3 py-2 text-left">Started</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {runsQuery.loading && (runsQuery.data?.runs.length ?? 0) === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="flex items-center justify-center py-10 text-slate-400 text-xs">
                        <Loader2 size={14} className="animate-spin mr-2" />
                        Loading runs…
                      </div>
                    </td>
                  </tr>
                ) : (runsQuery.data?.runs.length ?? 0) === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <EmptyState
                        title="No garden runs yet"
                        hint="Trigger a run from the sidebar to start the first cycle."
                        icon={<Workflow size={18} />}
                      />
                    </td>
                  </tr>
                ) : (
                  runsQuery.data?.runs.map((r) => (
                    <RunRow
                      key={r.run_id}
                      run={r}
                      onClick={() => openDetail(r.run_id)}
                    />
                  ))
                )}
              </tbody>
            </table>
            {(runsQuery.data?.count ?? 0) >= limit && (
              <button
                type="button"
                onClick={() => setLimit((l) => l + PAGE_SIZE)}
                className="w-full px-3 py-2 text-xs font-medium text-teal-700 hover:bg-slate-50 border-t border-slate-100 bg-slate-50/50"
              >
                Load more
              </button>
            )}
          </div>
        </div>

        <aside className="col-span-3 space-y-3">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
              Trigger Run
            </h3>
            <div className="space-y-2">
              <TriggerButton
                label="Performance refresh"
                icon={<RefreshCw size={13} />}
                onClick={() => setTrigger('performance_refresh')}
              />
              <TriggerButton
                label="Scheduled rebuild"
                icon={<Workflow size={13} />}
                onClick={() => setTrigger('scheduled_rebuild')}
              />
              <TriggerButton
                label="Creative batch"
                icon={<Sprout size={13} />}
                onClick={() => setTrigger('creative_batch')}
              />
              <TriggerButton
                label="Compass directed…"
                icon={<CompassIcon size={13} />}
                onClick={() => setTrigger('compass_directed')}
              />
            </div>
          </div>
          <Link
            to={`/admin/ontology/${clientUid}/builds`}
            className="block bg-white border border-slate-200 rounded-xl shadow-sm p-4 hover:border-teal-300"
          >
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
              Extraction
            </h3>
            <p className="text-sm font-medium text-navy-900">
              View extraction dashboard →
            </p>
            {pendingCreatives > 0 && (
              <p className="text-[11px] text-amber-700 mt-1 font-mono">
                {pendingCreatives} pending
              </p>
            )}
          </Link>
        </aside>
      </div>

      {detailRunId && (
        <RunDetailPanel
          clientUid={clientUid}
          runId={detailRunId}
          onClose={closeDetail}
          onAction={refetchAll}
        />
      )}

      {trigger && (
        <TriggerRunDrawer
          clientUid={clientUid}
          runType={trigger}
          onCancel={() => setTrigger(null)}
          onSuccess={(runId) => {
            setTrigger(null);
            navigate(`/admin/garden/${clientUid}?run=${runId}`);
            runsQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

function HealthBar({
  lastRunPerType,
  pendingCreatives,
  schedule,
}: {
  lastRunPerType: Map<GardenRunType, GardenRun>;
  pendingCreatives: number;
  schedule?: Record<string, unknown>;
}) {
  const types: GardenRunType[] = [
    'creative_batch',
    'performance_refresh',
    'compass_directed',
    'scheduled_rebuild',
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-3 grid grid-cols-12 gap-3">
      {types.map((t) => {
        const last = lastRunPerType.get(t);
        return (
          <div
            key={t}
            className="col-span-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-100"
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <RunTypeBadge runType={t} />
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Last:{' '}
              <span className="font-mono text-navy-900">
                {last ? timeAgo(last.started_at) : '–'}
              </span>
              {last && (
                <span className="ml-1">
                  <RunStatusBadge status={last.status} />
                </span>
              )}
            </p>
          </div>
        );
      })}

      <div className="col-span-2 px-3 py-1.5 bg-purple-50 rounded-lg border border-purple-100">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-purple-600">
          Pending creatives
        </p>
        <p className="text-base font-mono font-semibold text-purple-700">
          {formatNumber(pendingCreatives)}
        </p>
      </div>

      <div className="col-span-2 px-3 py-1.5 bg-slate-50 rounded-lg border border-slate-100">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">
          Schedule
        </p>
        <p className="text-[11px] text-slate-700 font-mono truncate">
          {schedule
            ? formatScheduleSummary(schedule)
            : '–'}
        </p>
      </div>
    </div>
  );
}

function formatScheduleSummary(schedule: Record<string, unknown>): string {
  // Common keys we expect; fall back to the first 2 entries verbatim.
  const cron = schedule.cron ?? schedule.expression ?? schedule.schedule;
  if (typeof cron === 'string') return cron;
  const enabled = schedule.enabled;
  if (typeof enabled === 'boolean') return enabled ? 'enabled' : 'disabled';
  const entries = Object.entries(schedule).slice(0, 2);
  return entries.map(([k, v]) => `${k}=${String(v)}`).join(' ');
}

function RunRow({
  run,
  onClick,
}: {
  run: GardenRun;
  onClick: () => void;
}) {
  const totalStages = RUN_TYPE_STAGES[run.run_type] ?? 8;
  const progress =
    run.status === 'running' && totalStages > 0
      ? Math.round(((run.stages_completed?.length ?? 0) / totalStages) * 100)
      : null;
  const duration = durationFromTimestamps(run.started_at, run.finished_at);

  return (
    <tr
      onClick={onClick}
      className="hover:bg-slate-50 cursor-pointer transition-colors"
    >
      <td className="px-3 py-2.5 font-mono text-xs text-navy-900">
        {run.run_id}
      </td>
      <td className="px-3 py-2.5">
        <RunTypeBadge runType={run.run_type} />
      </td>
      <td className="px-3 py-2.5">
        <div className="flex items-center gap-2">
          <RunStatusBadge status={run.status} />
          {run.status === 'running' && (
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-[10px] font-mono text-amber-700 truncate max-w-[120px]">
                {run.current_stage ?? '…'}
              </span>
              {progress !== null && (
                <div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">
        {formatDuration(duration)}
      </td>
      <td className="px-3 py-2.5 text-right font-mono text-xs">
        <span className="text-emerald-600">+{formatNumber(run.diff_summary.concepts_added)}</span>
        <span className="mx-0.5 text-slate-400">/</span>
        <span className="text-red-600">−{formatNumber(run.diff_summary.concepts_removed)}</span>
      </td>
      <td
        className={clsx(
          'px-3 py-2.5 text-right font-mono text-xs',
          (run.diff_summary.auc_delta ?? 0) > 0 && 'text-emerald-600',
          (run.diff_summary.auc_delta ?? 0) < 0 && 'text-red-600',
        )}
      >
        {run.diff_summary.auc_delta != null
          ? formatDelta(run.diff_summary.auc_delta, 3)
          : '–'}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-500">
        <span className="capitalize">{run.trigger_source}</span>
        {run.trigger_id && run.trigger_id !== 'manual' && (
          <span className="text-slate-400 ml-1 font-mono text-[10px]">
            {run.trigger_id}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-xs text-slate-500">
        {timeAgo(run.started_at)}
      </td>
    </tr>
  );
}

function TriggerButton({
  label,
  icon,
  onClick,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 text-slate-700"
    >
      <Play size={11} className="text-slate-400" />
      {icon}
      <span className="flex-1 text-left">{label}</span>
      <Plus size={11} className="text-slate-400" />
    </button>
  );
}
